import 'react-dates/initialize';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRight, faCalendar } from '@fortawesome/free-solid-svg-icons';
import { FocusedInputShape, ModifiersShape } from 'react-dates';
import { HORIZONTAL_ORIENTATION, START_DATE, END_DATE } from 'react-dates/constants';
import React from 'react';
import moment from 'moment';
import autobind from 'autobind-decorator';
import { YYYYMMDD, setToStartOfDay } from '../../../shared/utils/date';
import CalendarInfo from '../CalendarInfo';
import { Breakpoints, Colors } from '../../../shared/styles/StyleConst';
import i18n from '../../../R/string/i18n';
import { isEnglish } from '../../../config/locales/i18n';
import CalendarDayContent from '../CalendarDayContents';
import { CalendarPrice } from '../../../data_sources/networks/yado-backend/YadoApi';
import { numberWithCommas } from '../../../legacy/shared/utils/string';
import ExchangeRateStore from '../../../models/mobx/stores/ExchangeRateStore';
import { isJalanYado } from '../../../helpers/yado_helper';
import WAmazingDateRangePicker from '../../common/WAmazingDateRangePicker';

type Props = {
  exchangeRateStore: ExchangeRateStore;
  checkinDate: string | Null;
  checkoutDate: string | Null;
  onComplete: (checkinDate: string, checkoutDate: string) => void;
  calendarPrices: CalendarPrice[] | Null;
  onUpdateCalendar: (currentCalendarMonth: string) => void;
  isCalendarModalOpen: boolean;
  isLoadingCalendar: boolean;
  yadoId: string;
};

type State = {
  focusedInput: FocusedInputShape | null;
  uiStartDate: moment.Moment | null;
  uiEndDate: moment.Moment | null;
  screenSize: 'extraSmall' | 'small' | 'normal' | 'desktop';
};

const EXTRA_SMALL_WIDTH = 365;
const SMALL_WIDTH = 576;
const MEDIUM_WIDTH = 768;

class DateRangeInput extends React.Component<Props, State> {
  private lockUpdate = false;

  constructor(props: Props) {
    super(props);
    const { checkoutDate, checkinDate } = props;
    this.state = {
      focusedInput: null,
      uiStartDate: checkinDate ? moment(checkinDate) : null,
      uiEndDate: checkoutDate ? moment(checkoutDate) : null,
      screenSize: 'desktop',
    };
  }

  componentDidMount() {
    window.addEventListener('resize', this.onWindowResize);
    this.onWindowResize();
  }

  componentDidUpdate(prevProps: Props) {
    if (
      !prevProps.isCalendarModalOpen &&
      prevProps.isCalendarModalOpen !== this.props.isCalendarModalOpen
    ) {
      this.setState({
        focusedInput: START_DATE,
      });
    }

    if (prevProps.checkinDate !== this.props.checkinDate) {
      this.setState({
        uiStartDate: this.props.checkinDate ? moment(this.props.checkinDate) : null,
      });
    }

    if (prevProps.checkoutDate !== this.props.checkoutDate) {
      this.setState({
        uiEndDate: this.props.checkoutDate ? moment(this.props.checkoutDate) : null,
      });
    }
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.onWindowResize);
  }

  @autobind
  onChangeDate(dates: { startDate: moment.Moment | Null; endDate: moment.Moment | Null }) {
    const { startDate, endDate } = dates;

    if (startDate) {
      this.setState({
        uiStartDate: startDate,
        uiEndDate: null,
      });
    }

    if (endDate && this.state.focusedInput === END_DATE) {
      this.setState({
        focusedInput: START_DATE,
        uiEndDate: endDate,
      });
    }
  }

  @autobind
  onClose(checkinDate: moment.Moment | Null, checkoutDate: moment.Moment | Null) {
    const { onComplete } = this.props;
    if (!checkinDate) return;
    this.setState(
      {
        focusedInput: null,
        uiStartDate: checkinDate,
        uiEndDate: checkoutDate || checkinDate.clone().add(1, 'days'),
      },
      () => {
        const { uiEndDate, uiStartDate } = this.state;

        if (uiStartDate && uiEndDate) {
          onComplete(uiStartDate.format(YYYYMMDD), uiEndDate.format(YYYYMMDD));
        }
      },
    );
  }

  @autobind
  onWindowResize() {
    if (window) {
      if (window.innerWidth <= EXTRA_SMALL_WIDTH) {
        this.setState({
          screenSize: 'extraSmall',
        });
        return;
      }

      if (window.innerWidth <= SMALL_WIDTH) {
        this.setState({
          screenSize: 'small',
        });
        return;
      }
      if (window.innerWidth <= MEDIUM_WIDTH) {
        this.setState({
          screenSize: 'normal',
        });
        return;
      }
      this.setState({
        screenSize: 'desktop',
      });
    }
  }

  @autobind
  onFocusChange(focusedInput: FocusedInputShape | null) {
    (document.activeElement as HTMLElement)?.blur(); // スマホでキーボードが一瞬表示されるのを防止
    this.setState({
      focusedInput,
    });
  }

  get isSmallScreen() {
    return this.state.screenSize === 'small';
  }

  get isExtraSmallScreen() {
    return this.state.screenSize === 'extraSmall';
  }

  @autobind
  pricesDisplayFormat(day: moment.Moment, modifiers: ModifiersShape) {
    if (
      isJalanYado(this.props.yadoId) ||
      !this.props.calendarPrices ||
      modifiers.has('blocked-out-of-range')
    )
      return;

    const priceJpy = this.props.calendarPrices.find(
      item => item.date === moment(day.toDate()).format('YYYY-MM-DD'),
    )?.price;

    if (priceJpy) {
      const price = this.props.exchangeRateStore!.userExchangeRate?.exchange(priceJpy);
      return price && price >= 10000
        ? `${(Math.floor(price / 1000) / 10).toFixed(1)}${i18n.webYadoTenThousand()}`
        : numberWithCommas(price);
    }
    return '-';
  }

  @autobind
  updateCurrentCalendarMonth(action: string, newCurrentMonth: moment.Moment) {
    if (!this.lockUpdate) {
      this.lockUpdate = true;
      const { onUpdateCalendar } = this.props;
      const { isSmallScreen } = this;

      const currentCalendarMonth = newCurrentMonth;

      if (!isSmallScreen) {
        newCurrentMonth.add({ month: action === 'next' ? 1 : 0 });
      }

      if (currentCalendarMonth.isSame(moment(new Date()), 'month')) {
        onUpdateCalendar(moment(new Date()).format('YYYY-MM-DD'));
      } else if (currentCalendarMonth.isAfter(moment(new Date()), 'month')) {
        onUpdateCalendar(currentCalendarMonth.format('YYYY-MM-01'));
      }

      setTimeout(() => {
        this.lockUpdate = false;
      }, 1000);
    }
  }

  render() {
    const { uiStartDate, uiEndDate } = this.state;
    const { isSmallScreen } = this;
    const displayFormat = isSmallScreen ? 'M/DD' : 'M/DD(ddd)';
    const monthFormat = 'YYYY MMMM';
    return (
      <div className="d-flex align-items-center date-range-input-2">
        <WAmazingDateRangePicker
          displayFormat={displayFormat}
          monthFormat={monthFormat}
          numberOfMonths={isSmallScreen ? 1 : 2}
          orientation={HORIZONTAL_ORIENTATION}
          withFullScreenPortal={isSmallScreen}
          startDateId="checkinDate"
          endDateId="checkoutDate"
          startDatePlaceholderText={i18n.webYadoCheckinDate()}
          endDatePlaceholderText={i18n.webYadoCheckoutDate()}
          minDate={moment().startOf('month')}
          startDate={uiStartDate}
          endDate={uiEndDate}
          focusedInput={this.state.focusedInput} // required
          onFocusChange={focusedInput => this.onFocusChange(focusedInput)}
          onDatesChange={this.onChangeDate}
          onClose={({ startDate, endDate }) => this.onClose(startDate, endDate)}
          calendarInfoPosition="bottom"
          renderCalendarInfo={() => (
            <CalendarInfo
              displayFormat={displayFormat}
              uiStartDate={uiStartDate}
              uiEndDate={uiEndDate}
              onClose={this.onClose}
              isMobileWidth={isSmallScreen}
              withoutPrice={isJalanYado(this.props.yadoId)}
            />
          )}
          renderDayContents={(day, modifiers) => {
            return (
              <CalendarDayContent
                day={day}
                modifiers={modifiers}
                calendarPrice={this.pricesDisplayFormat(day, modifiers)}
                isLoadingCalendar={this.props.isLoadingCalendar}
              />
            );
          }}
          onPrevMonthClick={newCurrentMonth =>
            this.updateCurrentCalendarMonth('prev', newCurrentMonth)
          }
          onNextMonthClick={newCurrentMonth =>
            this.updateCurrentCalendarMonth('next', newCurrentMonth)
          }
          noBorder
          required
          disableScroll={isSmallScreen}
          daySize={this.state.screenSize === 'desktop' ? 52 : 50}
          verticalSpacing={0}
          hideKeyboardShortcutsPanel
          keepOpenOnDateSelect={isSmallScreen}
          firstDayOfWeek={isEnglish() ? 1 : 0}
          customInputIcon={
            <div className="d-none d-md-block">
              <FontAwesomeIcon
                icon={faCalendar}
                size="1x"
                style={{
                  color: '#686868',
                  marginLeft: -16,
                }}
              />
            </div>
          }
          customArrowIcon={
            <FontAwesomeIcon
              icon={faArrowRight}
              size="sm"
              style={{
                color: '#cecece',
              }}
            />
          }
        />
        <div className="night d-none d-md-flex px-4 px-lg-5 align-items-center justify-content-center">
          {i18n.yadoSearchStayingNight({
            count:
              uiEndDate && uiStartDate
                ? setToStartOfDay(uiEndDate).diff(setToStartOfDay(uiStartDate), 'days')
                : 0,
          })}
        </div>
        <style jsx>
          {`
            .night {
              border-left: solid 1px ${Colors.gray300};
              border-right: solid 1px ${Colors.gray300};
              color: #333;
              font-size: 14px;
              font-weight: 700;
              height: 8px;
              letter-spacing: 0.89px;
              white-space: nowrap;
              padding-top: 10px;
              padding-bottom: 10px;
            }
          `}
        </style>
        <style jsx global>
          {`
            .date-range-input-2 {
              position: relative;
            }
            .date-range-input-2 .DateRangePicker {
              width: 100%;
              height: 100%;
              display: flex;
            }
            .date-range-input-2 .DateRangePicker > div {
              flex: 1;
            }
            .date-range-input-2 .DayPicker_transitionContainer {
              min-height: 395px !important;
            }
            .date-range-input-2 .DateRangePickerInput {
              background-color: transparent !important;
              display: flex;
              width: 100%;
              height: 100%;
              justify-content: space-between;
            }
            .date-range-input-2 .DateInput {
              width: auto;
              min-width: 80px;
            }
            .date-range-input-2 .DateRangePickerInput_arrow {
              align-items: center;
              display: flex;
              justify-content: center;
            }
            .date-range-input-2 .DateInput_input {
              color: #333;
              font-size: 14px;
              letter-spacing: 1.03px;
              padding: 9px 12px 7px;
              max-width: 160px;
              font-weight: 700;
              text-align: center;
              cursor: pointer;
            }
            .date-range-input-2 .DateInput_input::placeholder {
              color: ${Colors.gray600};
            }
            .date-range-input-2 .DateInput_input__focused {
              background-color: transparent !important;
              border-bottom: 2px solid #f06083;
            }
            .date-range-input-2 .DayPickerNavigation_button__horizontal {
              padding: 6px;
              border-radius: 50%;
              z-index: 1;
            }
            .date-range-input-2 .DayPickerNavigation_leftButton__horizontalDefault {
              left: 30px;
            }
            .date-range-input-2 .DayPickerNavigation_rightButton__horizontalDefault {
              right: 30px;
            }
            .date-range-input-2 .CalendarMonth_caption {
              font-size: 16px;
              font-weight: bold;
              color: #333333;
              letter-spacing: 1.18px;
              padding-bottom: 44px;
            }
            .date-range-input-2 .DayPicker_weekHeader {
              height: 28px;
              top: 61px;
            }
            .date-range-input-2 .DayPicker_weekHeader_ul {
              background: #f8f8f8;
              font-weight: 500;
              color: #545454;
              height: 100%;
            }
            .date-range-input-2 .CalendarDay__default {
              font-size: 15px;
              color: #333333;
            }
            .date-range-input-2 .CalendarDay__firstDayOfWeek {
              color: ${isEnglish() ? '#333333' : '#4fa3fa'};
            }
            .date-range-input-2 .CalendarDay__lastDayOfWeek {
              color: #4fa3fa;
            }
            .date-range-input-2 td:nth-child(6) {
              color: ${isEnglish() ? '#4fa3fa' : '#333333'};
            }
            .date-range-input-2 .CalendarDay__blocked_out_of_range {
              color: #a8a8a8 !important;
              background: #fafafa !important;
            }
            .date-range-input-2 .CalendarDay__default:hover {
              background-color: #fff2f5;
              border: 1px solid #fff2f5;
            }
            .date-range-input-2 .CalendarDay__blocked_out_of_range:hover {
              color: #a8a8a8 !important;
              background: #fafafa !important;
              border: 1px solid #e4e7e7;
            }
            .date-range-input-2 .CalendarDay__selected_span {
              background-color: #fa8aa5;
              color: #fff !important;
              border: 1px solid #fa8aa5;
            }
            .date-range-input-2 .CalendarDay__hovered_span {
              background-color: #fff2f5;
              border: 1px solid #fff2f5;
            }
            .date-range-input-2 .CalendarDay__selected,
            .CalendarDay__selected:hover {
              background-color: #f06083 !important;
              border: 1px solid #f06083;
              color: #fff !important;
            }
            .date-range-input-2 .CalendarDay__default:active {
              background-color: #ffd1dc !important;
            }
            .date-range-input-2 .CalendarDay__selected_span:hover {
              background-color: #ff6b8e !important;
              border: 1px solid #ff6b8e;
            }
            @media screen and (max-width: ${Breakpoints.xsMax}) {
              .date-range-input-2 .DateInput {
                min-width: unset;
              }

              .date-range-input-2 .DateInput_input {
                font-size: 12px;
                max-width: 88px;
                padding: 9px 0px 7px;
                text-align: center;
              }
              :lang(en) .date-range-input-2 .DateInput_input {
                font-size: 10px;
              }
              .date-range-input-2 .DateRangePickerInput_calendarIcon {
                display: none;
              }

              .date-range-input-2 .DateRangePickerInput_arrow {
                margin: 0 4px;
              }
            }
          `}
        </style>
      </div>
    );
  }
}

export default DateRangeInput;
